import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";

import * as Api from "@/api";
import { NEW_PHONE_NUMBER } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputCopy from "@/design/forms/InputCopy";
import InputPhone from "@/design/forms/InputPhone";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import StringForm from "@/design/forms/StringForm";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode, SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viSimCard, viXmark } from "@sippy-platform/valkyrie";

import { Button, Checkbox, Dialog, FormControlLabel, Grid, Stack } from "@mui/material";
import { red } from "@mui/material/colors";
import { useQueryClient } from "@tanstack/react-query";

import phoneNumberReducer from "./phoneNumberReducer";

export default function PhoneNumber() {
  const navigate = useNavigate();
  const { did } = useParams();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [doCreateMore, setDoCreateMore] = useState(false);
  const [isRange, setIsRange] = useState(false);

  const auth = useSelector((state) => state.auth);
  const partnerStore = useSelector((state) => state.backstage.partners);
  const pbxStore = useSelector((state) => state.backstage.pbxes);

  const newNumber = useMemo(() => did === "new", [did]);

  useEffect(() => {
    async function getData() {
      toggleLoadingState(true);

      if (auth.accessLevel === 0) {
        await Promise.all([Api.getPbxes(), Api.getPartners()]).finally(toggleLoadingState);
      } else {
        await Promise.resolve(Api.getPbxes()).finally(toggleLoadingState);
      }
    }

    getData();
  }, [auth.accessLevel]);

  const {
    value: phoneNumber,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PHONE_NUMBER,
    mode: SaveMode.MANUAL,
    reducer: phoneNumberReducer,
    validator: () => ({
      start: string()
        .required({
          message: isRange ? "Start number is required." : "Number is required.",
          condition: newNumber,
        })
        .startsWith("+", {
          message: isRange
            ? "Start number must use the international format."
            : "Number must use the international format.",
          condition: newNumber,
        }),
      end: string()
        .required({
          message: "End number is required.",
          condition: newNumber && isRange,
        })
        .startsWith("+", {
          message: "End number must use the international format.",
          condition: newNumber && isRange,
        })
        .min(Number(phoneNumber.start) + 1, {
          message: "End number must be a number between 1 and 99 higher than start number.",
          condition: newNumber && isRange,
        })
        .max(Number(phoneNumber.start) + 99, {
          message: "End number must be a number between 1 and 99 higher than start number.",
          condition: newNumber && isRange,
        }),
    }),
    onSubmit: () => Api.storePhoneNumber(phoneNumber),
    onSuccess: () => {
      if (newNumber && !doCreateMore) {
        navigate(`/backstage/phone-numbers?${searchParams.toString()}`);
      } else if (newNumber && doCreateMore) {
        dispatch({ type: "reset-more" });
        setIsRange(false);
      }

      queryClient.invalidateQueries({
        queryKey: ["phonenumbers"],
        exact: false,
      });
    },
  });

  // Get number
  useEffect(() => {
    if (did !== "new" && did) {
      Api.cancelActiveGetRequests();

      Promise.resolve(Api.getPhoneNumber({ did })).then((response) =>
        dispatch({ type: "set", value: response.data.phoneNumber }),
      );
    } else {
      dispatch({ type: "set", value: NEW_PHONE_NUMBER });
    }
  }, [did, dispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "pbx":
        dispatch({ type: property, value, pbxStore });
        break;
      case "range":
        dispatch({ type: "start", value: phoneNumber.start, isRange: value });
        setIsRange(value);
        break;
      default:
        dispatch({ type: property, value, isRange });
    }

    if (!newNumber) setSaveState(SaveState.UNSAVED);
  };

  function onDeleteFinish() {
    navigate(`/backstage/phone-numbers?${searchParams.toString()}`);
    queryClient.invalidateQueries({ queryKey: ["phonenumbers"], exact: false });
  }

  return (
    <Dialog open={!!did} onClose={() => navigate(`/backstage/phone-numbers?${searchParams.toString()}`)} maxWidth="sm">
      <HeaderCard
        avatar={<Avatar text={phoneNumber.did} icon={viSimCard} />}
        action={
          auth.accessLevel === 0 &&
          phoneNumber && (
            <DeleteFlow
              key={phoneNumber.did || "nk"}
              isActionable={!!phoneNumber?.did}
              onDelete={() => Api.deletePhoneNumber(phoneNumber)}
              name={phoneNumber.did}
              onDeleteFinish={onDeleteFinish}
              deleteInfo="Are you sure you want to delete this phone number? This will remove all references to this number from the PBX and can't be undone."
            />
          )
        }
        title={phoneNumber.did || "Add phone numbers"}
      />
      <Stack
        sx={{
          gap: 2,
          mt: 2,
        }}
      >
        {phoneNumber?.did && (
          <FormGroup title="Details">
            <Grid size={12}>
              <InputCopy id="phonenumber" label="Phone number" value={phoneNumber.did} />
              <StringForm
                label="Inbound route"
                text={
                  phoneNumber?.inbound ? (
                    phoneNumber.inbound.name
                  ) : (
                    <Valkyrie icon={viXmark} style={{ color: red[500] }} />
                  )
                }
              />
              <StringForm label="Type" text={phoneNumber.type === "mobile" ? "Mobile" : "Fixed"} />
            </Grid>
          </FormGroup>
        )}
        {did === "new" && (
          <FormGroup title="Details">
            <Grid size={12}>
              <InputToggle
                id="range"
                onChange={formHandler}
                states={[false, true]}
                value={isRange}
                primary="Add a range"
                error={errors}
              />
            </Grid>
            <Grid size={12}>
              <InputPhone
                required
                id="start"
                label={isRange ? "Start number" : "Number"}
                onChange={formHandler}
                value={phoneNumber.start}
                error={errors}
              />
            </Grid>
            {isRange && (
              <Grid size={12}>
                <InputPhone
                  required
                  id="end"
                  label="End number"
                  onChange={formHandler}
                  value={phoneNumber.end}
                  error={errors}
                  helperText={
                    Number(phoneNumber.end) - Number(phoneNumber.start) >= 1 &&
                    Number(phoneNumber.end) - Number(phoneNumber.start) <= 99
                      ? `You are about to add ${Number(phoneNumber.end) - Number(phoneNumber.start) + 1} numbers.`
                      : undefined
                  }
                />
              </Grid>
            )}
            <Grid size={12}>
              <InputSelect
                id="type"
                label="Type"
                onChange={formHandler}
                value={phoneNumber.type}
                selects={[
                  { value: "mobile", label: "Mobile" },
                  { value: "fixed", label: "Fixed" },
                ]}
                getOptionLabel={(option) => option.label}
                error={errors}
              />
            </Grid>
          </FormGroup>
        )}
        <FormGroup title="Owner">
          {auth.accessLevel === 0 && (
            <Grid size={12}>
              <InputAutocomplete
                id="partner"
                label="Partner"
                onChange={formHandler}
                value={phoneNumber.partner?.id}
                selectValue="id"
                selects={partnerStore.list}
                getOptionLabel={(option) => option.name}
                hasEmpty={null}
                error={errors}
              />
            </Grid>
          )}
          <Grid size={12}>
            <InputAutocomplete
              id="pbx"
              label="PBX"
              onChange={formHandler}
              value={phoneNumber.pbx?.id}
              selectValue="id"
              selects={pbxStore.list.filter((pbx) =>
                phoneNumber.partner?.id ? pbx.partner.id === phoneNumber.partner.id : true,
              )}
              getOptionLabel={(option) => option.name}
              hasEmpty={null}
              error={errors}
              helperText="Changing the PBX will remove any references to this number from the previous PBX, proceed with caution."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Comments">
          {auth.accessLevel === 0 ? (
            <>
              <Grid size={12}>
                <InputText
                  id="adminComment"
                  label="Comment"
                  onChange={formHandler}
                  value={phoneNumber.adminComment}
                  error={errors}
                />
              </Grid>
              {phoneNumber.partnerComment && <StringForm label="Partner comment" text={phoneNumber.partnerComment} />}
            </>
          ) : (
            <Grid size={12}>
              <InputText
                id="partnerComment"
                label="Comment"
                onChange={formHandler}
                value={phoneNumber.partnerComment}
                error={errors}
              />
            </Grid>
          )}
        </FormGroup>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            containerType: "inline-size",
          }}
        >
          <Button variant="outlined" onClick={() => navigate(`/backstage/phone-numbers?${searchParams.toString()}`)}>
            Cancel
          </Button>
          <Stack direction="row">
            {newNumber && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={doCreateMore}
                    onChange={() => setDoCreateMore((prev) => !prev)}
                    sx={{ p: 1 }}
                  />
                }
                label="Create more"
                sx={{
                  ".MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
            <SaveIndicator formState={formState} errors={allErrors} save={save} />
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
}
